/**
 * Offline boot.
 *
 * The tablet and the phones must open with zero network. The local mirror
 * (Dexie) already holds all the DATA; this is about the app SHELL — the HTML,
 * JS and CSS needed to render anything at all.
 *
 * Deliberately hand-rolled rather than a plugin: the update flow is the risky
 * part on an unattended wall display, and it is worth being able to read it.
 */

export const SW_URL = '/sw.js'

export type SwState = 'unsupported' | 'registering' | 'ready' | 'update-ready' | 'failed'

export interface SwStatus {
  state: SwState
  /** Set when a new version is waiting. The board applies it when idle. */
  waiting: ServiceWorker | null
  error: string | null
}

export function swSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator &&
    typeof window !== 'undefined' && window.isSecureContext
}

/**
 * Register, and report when an update is waiting.
 *
 * The update is NOT applied immediately. A wall display that reloads itself
 * mid-glance is worse than one that is a version behind; the caller decides
 * when to apply it (docs/02 §2: shipping an update must not brick a kiosk
 * nobody is watching).
 */
export async function registerServiceWorker(
  onStatus: (s: SwStatus) => void, url = SW_URL,
): Promise<ServiceWorkerRegistration | null> {
  if (!swSupported()) {
    onStatus({ state: 'unsupported', waiting: null, error: null })
    return null
  }
  onStatus({ state: 'registering', waiting: null, error: null })
  try {
    const reg = await navigator.serviceWorker.register(url, { scope: '/' })

    if (reg.waiting) onStatus({ state: 'update-ready', waiting: reg.waiting, error: null })
    else onStatus({ state: 'ready', waiting: null, error: null })

    reg.addEventListener('updatefound', () => {
      const next = reg.installing
      if (!next) return
      next.addEventListener('statechange', () => {
        // 'installed' with a controller already present means an UPDATE, not a
        // first install. A first install must never prompt anyone.
        if (next.state === 'installed' && navigator.serviceWorker.controller) {
          onStatus({ state: 'update-ready', waiting: next, error: null })
        }
      })
    })
    return reg
  } catch (e) {
    onStatus({ state: 'failed', waiting: null, error: (e as Error).message })
    return null
  }
}

/** Apply a waiting update and reload. Call this when the screen is idle. */
export function applyUpdate(waiting: ServiceWorker): void {
  waiting.postMessage({ type: 'SKIP_WAITING' })
  // The controllerchange fires once the new worker takes over.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload()
  }, { once: true })
}

/**
 * Ask the browser to keep our storage.
 *
 * Expect FALSE inside a WebView: there is no site-engagement score, bookmark,
 * installed PWA or notification permission to satisfy Chromium's durable-storage
 * heuristics (docs/02 §2). The answer is informational — never gate behaviour on
 * it, and never tell the user their data is safe because of it.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false
    if (await navigator.storage.persisted?.()) return true
    return await navigator.storage.persist()
  } catch {
    return false
  }
}

export interface StorageEstimate { usedBytes: number; quotaBytes: number; ratio: number }

export async function storageEstimate(): Promise<StorageEstimate | null> {
  try {
    if (!navigator.storage?.estimate) return null
    const e = await navigator.storage.estimate()
    const used = e.usage ?? 0
    const quota = e.quota ?? 0
    return { usedBytes: used, quotaBytes: quota, ratio: quota > 0 ? used / quota : 0 }
  } catch {
    return null
  }
}
